import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ArrowLeft,
  CalendarDays,
  X,
} from 'lucide-react';

import {
  useNavigate,
} from 'react-router-dom';

import RouteConfig
  from '../../../routes/RouteConfig';

import TournamentCalendar
  from '../components/TournamentCalendar';

import TournamentCard
  from '../components/TournamentCard';

import {
  useTournamentSchedule,
  type TournamentViewItem,
} from '../hooks/useTournamentSchedule';


/* =========================================================
   Types
   ========================================================= */

type CalendarDay = {
  day: number;
  dateKey: string;
  isCurrentMonth: boolean;
};


type PreviewImage = {
  src: string;
  alt: string;
};


/* =========================================================
   Utils
   ========================================================= */

const formatDateKey = (
  year: number,
  month: number,
  day: number,
): string => {

  return `${year}-${String(
    month + 1,
  ).padStart(
    2,
    '0',
  )}-${String(
    day,
  ).padStart(
    2,
    '0',
  )}`;
};


const formatMonthKey = (
  year: number,
  month: number,
): string => {

  return `${year}-${String(
    month + 1,
  ).padStart(
    2,
    '0',
  )}`;
};


const formatKoreanDate = (
  dateString: string,
): string => {

  const date =
    new Date(
      `${dateString}T00:00:00`,
    );


  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return dateString;
  }


  return date.toLocaleDateString(
    'ko-KR',
    {
      year:
        'numeric',

      month:
        'long',

      day:
        'numeric',

      weekday:
        'short',
    },
  );
};


/* =========================================================
   Page
   ========================================================= */

const TournamentDetailPage:
  React.FC = () => {

    const navigate =
      useNavigate();


    /* =====================================================
       Tournament State / API
       ===================================================== */

    const {
      tournamentMap,

      isLoading,
      errorMessage,

      reservationStatus,
      reservationLoadingId,

      loadCurrentUser,
      loadMonthlyTournaments,

      reserve,
    } =
      useTournamentSchedule();


    /* =====================================================
       Today
       ===================================================== */

    /**
     * 페이지 최초 진입 시점을
     * 오늘 날짜 기준으로 고정합니다.
     */
    const today =
      useMemo(
        () =>
          new Date(),
        [],
      );


    const todayKey =
      useMemo(
        () =>
          formatDateKey(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
          ),
        [
          today,
        ],
      );


    /**
     * 오늘은 선택 불가이므로
     * 최초 선택 날짜는 내일입니다.
     */
    const tomorrow =
      useMemo(
        () =>
          new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() +
              1,
          ),
        [
          today,
        ],
      );


    const tomorrowKey =
      useMemo(
        () =>
          formatDateKey(
            tomorrow.getFullYear(),
            tomorrow.getMonth(),
            tomorrow.getDate(),
          ),
        [
          tomorrow,
        ],
      );


    /* =====================================================
       Calendar State
       ===================================================== */

    const [
      currentDate,
      setCurrentDate,
    ] =
      useState(
        () =>
          new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
          ),
      );


    const [
      selectedDate,
      setSelectedDate,
    ] =
      useState(
        tomorrowKey,
      );


    const currentYear =
      currentDate.getFullYear();


    const currentMonth =
      currentDate.getMonth();


    /* =====================================================
       Bottom Sheet / Image
       ===================================================== */

    const [
      isBottomSheetOpen,
      setIsBottomSheetOpen,
    ] =
      useState(
        false,
      );


    const [
      previewImage,
      setPreviewImage,
    ] =
      useState<
        PreviewImage | null
      >(
        null,
      );


    /* =====================================================
       Selected Tournaments
       ===================================================== */

    const selectedTournaments =
      useMemo<
        TournamentViewItem[]
      >(
        () =>
          tournamentMap.get(
            selectedDate,
          ) ??
          [],
        [
          tournamentMap,
          selectedDate,
        ],
      );


    /* =====================================================
       Calendar Days
       ===================================================== */

    const calendarDays =
      useMemo<
        CalendarDay[]
      >(
        () => {

          const firstDay =
            new Date(
              currentYear,
              currentMonth,
              1,
            ).getDay();


          const lastDate =
            new Date(
              currentYear,
              currentMonth +
                1,
              0,
            ).getDate();


          const previousMonthLastDate =
            new Date(
              currentYear,
              currentMonth,
              0,
            ).getDate();


          const days:
            CalendarDay[] =
              [];


          /**
           * 이전 달
           */
          for (
            let index =
              firstDay -
              1;

            index >=
            0;

            index -=
            1
          ) {

            const day =
              previousMonthLastDate -
              index;


            const date =
              new Date(
                currentYear,
                currentMonth -
                  1,
                day,
              );


            days.push({
              day,

              dateKey:
                formatDateKey(
                  date.getFullYear(),
                  date.getMonth(),
                  date.getDate(),
                ),

              isCurrentMonth:
                false,
            });
          }


          /**
           * 현재 달
           */
          for (
            let day = 1;

            day <=
            lastDate;

            day +=
            1
          ) {

            days.push({
              day,

              dateKey:
                formatDateKey(
                  currentYear,
                  currentMonth,
                  day,
                ),

              isCurrentMonth:
                true,
            });
          }


          /**
           * 다음 달
           *
           * 달력은 항상
           * 6주 × 7일 = 42칸
           */
          let nextMonthDay =
            1;


          while (
            days.length <
            42
          ) {

            const date =
              new Date(
                currentYear,
                currentMonth +
                  1,
                nextMonthDay,
              );


            days.push({
              day:
                date.getDate(),

              dateKey:
                formatDateKey(
                  date.getFullYear(),
                  date.getMonth(),
                  date.getDate(),
                ),

              isCurrentMonth:
                false,
            });


            nextMonthDay +=
              1;
          }


          return days;
        },
        [
          currentYear,
          currentMonth,
        ],
      );


    /* =====================================================
       Current User
       ===================================================== */

    useEffect(
      () => {

        void loadCurrentUser();

      },
      [
        loadCurrentUser,
      ],
    );


    /* =====================================================
       Monthly Tournament
       ===================================================== */

    const currentMonthKey =
      useMemo(
        () =>
          formatMonthKey(
            currentYear,
            currentMonth,
          ),
        [
          currentYear,
          currentMonth,
        ],
      );


    /**
     * =====================================================
     * 월별 대회 조회
     *
     * useTournamentSchedule 내부에서:
     *
     * 1. 월별 대회
     * 2. 내 예약 대회 목록
     *
     * 을 함께 조회하여 reservationStatus를 구성합니다.
     *
     * 따라서 페이지 재진입 / 새로고침 후에도
     * 예약완료 상태가 유지됩니다.
     * =====================================================
     */
    useEffect(
      () => {

        void loadMonthlyTournaments(
          currentMonthKey,
        );

      },
      [
        currentMonthKey,
        loadMonthlyTournaments,
      ],
    );


    /* =====================================================
       Navigation
       ===================================================== */

    const handleBack =
      (): void => {

        if (
          window.history.length >
          1
        ) {

          navigate(
            -1,
          );


          return;
        }


        navigate(
          RouteConfig
            .User
            .Home
            .fullPath,
          {
            replace:
              true,
          },
        );
      };


    const handlePreviousMonth =
      (): void => {

        setCurrentDate(
          new Date(
            currentYear,
            currentMonth -
              1,
            1,
          ),
        );
      };


    const handleNextMonth =
      (): void => {

        setCurrentDate(
          new Date(
            currentYear,
            currentMonth +
              1,
            1,
          ),
        );
      };


    /**
     * 오늘 버튼
     *
     * 오늘은 예약 불가 정책이므로
     * 실제 선택은 내일로 이동합니다.
     */
    const handleMoveToday =
      (): void => {

        setCurrentDate(
          new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
          ),
        );


        setSelectedDate(
          tomorrowKey,
        );


        setIsBottomSheetOpen(
          tournamentMap.has(
            tomorrowKey,
          ),
        );
      };


    /* =====================================================
       Date Selection
       ===================================================== */

    const handleDateClick = (
      dateKey: string,
    ): void => {

      /**
       * YYYY-MM-DD 형식이므로
       * 문자열 비교가 가능합니다.
       *
       * 오늘 포함 과거 날짜는
       * 선택할 수 없습니다.
       */
      if (
        dateKey <=
        todayKey
      ) {
        return;
      }


      setSelectedDate(
        dateKey,
      );


      const clickedDate =
        new Date(
          `${dateKey}T00:00:00`,
        );


      const clickedYear =
        clickedDate.getFullYear();


      const clickedMonth =
        clickedDate.getMonth();


      /**
       * 이전/다음 달 영역을
       * 눌렀을 경우 해당 월로 이동
       */
      if (
        clickedYear !==
          currentYear ||
        clickedMonth !==
          currentMonth
      ) {

        setCurrentDate(
          new Date(
            clickedYear,
            clickedMonth,
            1,
          ),
        );
      }


      /**
       * 해당 날짜에 대회가 있는 경우에만
       * Bottom Sheet 표시
       */
      setIsBottomSheetOpen(
        tournamentMap.has(
          dateKey,
        ),
      );
    };


    /* =====================================================
       Reservation
       ===================================================== */

    const handleReservation =
      async (
        tournament:
          TournamentViewItem,
      ): Promise<void> => {

        /**
         * =================================================
         * 이미 한 번 예약한 대회인지 확인
         *
         * useTournamentSchedule에서
         * 서버의 내 예약 목록을 기준으로
         * reservationStatus를 구성합니다.
         * =================================================
         */
        const isReserved =
          reservationStatus[
            tournament.id
          ] ??
          false;


        const isFull =
          tournament
            .reservedParticipants >=
          tournament
            .maxParticipants;


        const isReserving =
          reservationLoadingId ===
          tournament.id;


        /**
         * 이미 예약
         * 또는 예약 마감
         * 또는 현재 예약 처리 중이면
         * API 호출 금지
         */
        if (
          isReserved ||
          isFull ||
          isReserving
        ) {
          return;
        }


        const confirmed =
          window.confirm(
            [
              `${tournament.title} 대회를 예약하시겠습니까?`,

              '',

              `일시: ${formatKoreanDate(
                tournament.date,
              )} ${tournament.startTime}`,

              `장소: ${tournament.location}`,

              `필요 시드: ${tournament.buyIn.toLocaleString(
                'ko-KR',
              )}시드`,

              '',

              '대회 예약 취소가 필요한 경우 관리자에게 문의해주세요.',
            ].join(
              '\n',
            ),
          );


        if (
          !confirmed
        ) {
          return;
        }


        try {

          await reserve(
            tournament,
          );


          window.alert(
            '대회 예약이 완료되었습니다.',
          );

        } catch (
          error
        ) {

          console.error(
            '대회 예약 실패:',
            error,
          );


          window.alert(
            '대회 예약에 실패했습니다.',
          );

        }
      };


    /* =====================================================
       Preview Image
       ===================================================== */

    const handlePreviewImage = (
      src: string,
      alt: string,
    ): void => {

      setPreviewImage({
        src,
        alt,
      });
    };


    /* =====================================================
       ESC
       ===================================================== */

    useEffect(
      () => {

        const handleEscape = (
          event:
            KeyboardEvent,
        ): void => {

          if (
            event.key !==
            'Escape'
          ) {
            return;
          }


          if (
            previewImage
          ) {

            setPreviewImage(
              null,
            );


            return;
          }


          setIsBottomSheetOpen(
            false,
          );
        };


        window.addEventListener(
          'keydown',
          handleEscape,
        );


        return () => {

          window.removeEventListener(
            'keydown',
            handleEscape,
          );

        };
      },
      [
        previewImage,
      ],
    );


    /* =====================================================
       Body Scroll Lock
       ===================================================== */

    useEffect(
      () => {

        if (
          !isBottomSheetOpen &&
          !previewImage
        ) {
          return;
        }


        const previousOverflow =
          document
            .body
            .style
            .overflow;


        document.body.style.overflow =
          'hidden';


        return () => {

          document.body.style.overflow =
            previousOverflow;

        };
      },
      [
        isBottomSheetOpen,
        previewImage,
      ],
    );


    /* =====================================================
       Render
       ===================================================== */

    return (
      <div
        className="
          flex
          h-full
          min-h-0
          w-full
          flex-col
          overflow-hidden
          bg-gray-50
          px-3
          pb-3
          pt-14
          sm:px-4
          sm:pb-4
          md:px-5
          md:pt-4
          lg:px-6
        "
      >

        {/* =================================================
            Header
            ================================================= */}

        <header
          className="
            mx-auto
            mb-2
            flex
            w-full
            max-w-7xl
            shrink-0
            items-center
            justify-between
            gap-2
            sm:mb-3
            sm:gap-3
          "
        >

          <div
            className="
              flex
              min-w-0
              items-center
              gap-2
              sm:gap-3
            "
          >

            <button
              type="button"
              onClick={
                handleBack
              }
              className="
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center
                rounded-lg
                border
                border-gray-200
                bg-white
                text-gray-700
                shadow-sm
                transition
                hover:bg-gray-100
                active:scale-95
                sm:h-10
                sm:w-10
              "
              aria-label="이전 페이지로 돌아가기"
              title="뒤로 가기"
            >
              <ArrowLeft
                className="
                  h-5
                  w-5
                  sm:h-6
                  sm:w-6
                "
              />
            </button>


            <div
              className="
                min-w-0
              "
            >

              <div
                className="
                  flex
                  items-center
                  gap-1.5
                  text-main
                "
              >

                <CalendarDays
                  className="
                    h-4
                    w-4
                    shrink-0
                    sm:h-5
                    sm:w-5
                  "
                />


                <span
                  className="
                    hidden
                    text-xs
                    font-semibold
                    sm:inline
                  "
                >
                  Tournament Schedule
                </span>

              </div>


              <div
                className="
                  mt-0.5
                  flex
                  min-w-0
                  items-baseline
                  gap-2
                "
              >

                <h1
                  className="
                    shrink-0
                    text-xl
                    font-bold
                    text-gray-900
                    sm:text-2xl
                    lg:text-3xl
                  "
                >
                  대회 일정
                </h1>


                <p
                  className="
                    hidden
                    truncate
                    text-sm
                    text-gray-500
                    md:block
                  "
                >
                  날짜를 선택하면 대회 정보를 확인할 수 있습니다.
                </p>

              </div>

            </div>

          </div>


          <button
            type="button"
            onClick={
              handleMoveToday
            }
            className="
              inline-flex
              shrink-0
              items-center
              justify-center
              rounded-lg
              border
              border-gray-300
              bg-white
              px-3
              py-2
              text-xs
              font-semibold
              text-gray-700
              shadow-sm
              transition
              hover:bg-gray-100
              active:scale-95
              sm:px-4
              sm:text-sm
            "
          >
            오늘
          </button>

        </header>


        {/* =================================================
            Main Calendar
            ================================================= */}

        <main
          className="
            mx-auto
            flex
            min-h-0
            w-full
            max-w-7xl
            flex-1
            flex-col
          "
        >

          <div
            className="
              relative
              flex
              min-h-0
              flex-1
              flex-col
            "
          >

            {/* Loading Overlay */}

            {isLoading && (

              <div
                className="
                  absolute
                  inset-0
                  z-20
                  grid
                  place-items-center
                  rounded-2xl
                  bg-white/80
                  backdrop-blur-sm
                "
              >

                <div
                  className="
                    text-center
                  "
                >

                  <div
                    className="
                      mx-auto
                      h-8
                      w-8
                      animate-spin
                      rounded-full
                      border-2
                      border-gray-200
                      border-t-main
                    "
                  />


                  <p
                    className="
                      mt-3
                      text-sm
                      text-gray-500
                    "
                  >
                    대회 일정을 불러오고 있습니다.
                  </p>

                </div>

              </div>

            )}


            {/* Error Overlay */}

            {errorMessage &&
              !isLoading && (

                <div
                  className="
                    absolute
                    inset-0
                    z-20
                    grid
                    place-items-center
                    rounded-2xl
                    bg-white/90
                    px-4
                  "
                >

                  <div
                    className="
                      max-w-sm
                      text-center
                    "
                  >

                    <p
                      className="
                        text-sm
                        font-semibold
                        text-red-600
                      "
                    >
                      {errorMessage}
                    </p>


                    <button
                      type="button"
                      onClick={() =>
                        void loadMonthlyTournaments(
                          currentMonthKey,
                        )
                      }
                      className="
                        mt-4
                        rounded-lg
                        bg-gray-900
                        px-4
                        py-2
                        text-sm
                        font-semibold
                        text-white
                      "
                    >
                      다시 시도
                    </button>

                  </div>

                </div>

              )}


            <TournamentCalendar
              currentYear={
                currentYear
              }
              currentMonth={
                currentMonth
              }
              todayKey={
                todayKey
              }
              selectedDate={
                selectedDate
              }
              calendarDays={
                calendarDays
              }
              tournamentMap={
                tournamentMap
              }
              onPreviousMonth={
                handlePreviousMonth
              }
              onNextMonth={
                handleNextMonth
              }
              onDateClick={
                handleDateClick
              }
            />

          </div>


          {/* Selected Date Info */}

          <div
            className="
              mt-2
              flex
              min-h-9
              shrink-0
              items-center
              rounded-lg
              border
              border-gray-200
              bg-white
              px-3
              py-1.5
              text-xs
              text-gray-600
              shadow-sm
              sm:min-h-10
              sm:text-sm
            "
          >

            <span
              className="
                shrink-0
                font-semibold
                text-gray-900
              "
            >
              {formatKoreanDate(
                selectedDate,
              )}
            </span>


            <span
              className="
                ml-2
                truncate
              "
            >
              {selectedTournaments.length >
              0
                ? `대회 ${selectedTournaments.length}개 예정`
                : '예정된 대회가 없습니다.'}
            </span>

          </div>

        </main>


        {/* =================================================
            Bottom Sheet Overlay
            ================================================= */}

        <div
          className={`
            fixed
            inset-0
            z-40
            bg-black/50
            backdrop-blur-sm
            transition-opacity
            duration-300

            ${
              isBottomSheetOpen
                ? `
                  pointer-events-auto
                  opacity-100
                `
                : `
                  pointer-events-none
                  opacity-0
                `
            }
          `}
          onClick={() =>
            setIsBottomSheetOpen(
              false,
            )
          }
          aria-hidden="true"
        />


        {/* =================================================
            Bottom Sheet
            ================================================= */}

        <section
          role="dialog"
          aria-modal="true"
          aria-label="대회 상세 정보"
          className={`
            fixed
            inset-x-0
            bottom-0
            z-50
            flex
            max-h-[92dvh]
            transform
            flex-col
            overflow-hidden
            rounded-t-3xl
            bg-white
            shadow-2xl
            transition-transform
            duration-300
            ease-out

            ${
              isBottomSheetOpen
                ? 'translate-y-0'
                : 'translate-y-full'
            }
          `}
        >

          <div
            className="
              mx-auto
              flex
              min-h-0
              w-full
              max-w-4xl
              flex-1
              flex-col
            "
          >

            {/* Header */}

            <div
              className="
                shrink-0
                border-b
                border-gray-100
                px-4
                pb-3
                pt-2
                sm:px-6
              "
            >

              <div
                className="
                  mx-auto
                  h-1
                  w-10
                  rounded-full
                  bg-gray-300
                "
              />


              <div
                className="
                  mt-3
                  flex
                  items-center
                  justify-between
                  gap-4
                "
              >

                <div
                  className="
                    min-w-0
                  "
                >

                  <p
                    className="
                      text-[10px]
                      font-semibold
                      text-main
                      sm:text-xs
                    "
                  >
                    SELECTED DATE
                  </p>


                  <h2
                    className="
                      mt-0.5
                      truncate
                      text-lg
                      font-bold
                      text-gray-900
                      sm:text-xl
                    "
                  >
                    {formatKoreanDate(
                      selectedDate,
                    )}
                  </h2>

                </div>


                <button
                  type="button"
                  onClick={() =>
                    setIsBottomSheetOpen(
                      false,
                    )
                  }
                  className="
                    flex
                    h-9
                    w-9
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-gray-100
                    text-gray-600
                    transition
                    hover:bg-gray-200
                    active:scale-95
                  "
                  aria-label="대회 상세 정보 닫기"
                >
                  <X
                    size={20}
                  />
                </button>

              </div>

            </div>


            {/* Tournament Cards */}

            <div
              className="
                min-h-0
                flex-1
                overflow-y-auto
                px-3
                pb-[calc(16px+env(safe-area-inset-bottom))]
                pt-3
                sm:px-5
              "
            >

              <div
                className="
                  space-y-3
                "
              >

                {selectedTournaments.map(
                  (
                    tournament,
                  ) => {

                    /**
                     * =========================================
                     * 서버에서 받아온
                     * 사용자 예약 여부
                     *
                     * true:
                     * 이미 참가자 목록에 등록됨
                     * =========================================
                     */
                    const isReserved =
                      reservationStatus[
                        tournament.id
                      ] ??
                      false;


                    const isReserving =
                      reservationLoadingId ===
                      tournament.id;


                    return (
                      <TournamentCard
                        key={
                          tournament.id
                        }
                        tournament={
                          tournament
                        }
                        isReserved={
                          isReserved
                        }
                        isReserving={
                          isReserving
                        }
                        onReserve={() =>
                          void handleReservation(
                            tournament,
                          )
                        }
                        onPreviewImage={
                          handlePreviewImage
                        }
                      />
                    );
                  },
                )}

              </div>

            </div>

          </div>

        </section>


        {/* =================================================
            Image Preview
            ================================================= */}

        {previewImage && (

          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${previewImage.alt} 전체화면 이미지`}
            className="
              fixed
              inset-0
              z-[100]
              flex
              cursor-zoom-out
              items-center
              justify-center
              bg-black/90
              p-3
              backdrop-blur-sm
              sm:p-6
            "
            onClick={() =>
              setPreviewImage(
                null,
              )
            }
          >

            <img
              src={
                previewImage.src
              }
              alt={
                previewImage.alt
              }
              className="
                max-h-full
                max-w-full
                select-none
                object-contain
              "
              draggable={
                false
              }
            />

          </div>

        )}

      </div>
    );
  };


export default TournamentDetailPage;